import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getClientes, crearCliente } from "../../../services/usuarios_tienda/clientes";
import { getProductos } from "../../../services/usuarios_tienda/productos";
import { crearPedidoManual } from "../../../services/usuarios_tienda/pedidos";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import { formatearPrecio } from "../../../utils/moneda";
import { fileURL } from "../../../utils/fileURL";
import { hasPerm } from "../../../utils/hasPerm";
import CustomerFormModal from "../clientes/CustomerFormModal";
import "./CreateOrderPage.css";

const ORDERS_PATH = "/administracion/tienda/orders";

function precioBaseProducto(p) {
  const enOferta = Boolean(p?.en_oferta);
  const efectivo = Number(p?.precio_efectivo);
  if (enOferta && Number.isFinite(efectivo) && efectivo > 0) return efectivo;
  const base = Number(p?.precio_unitario);
  return Number.isFinite(base) ? base : 0;
}

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const cerrar = () => navigate(ORDERS_PATH);

  const [configuracionTienda, setConfiguracionTienda] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Cliente (solo existentes)
  const [clienteId, setClienteId] = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteOpen, setClienteOpen] = useState(false);
  const clienteComboRef = useRef(null);
  const [showClienteModal, setShowClienteModal] = useState(false);

  // Productos del pedido: [{ producto_id, nombre, cantidad, precio_unitario }]
  const [items, setItems] = useState([]);
  const [productoSearch, setProductoSearch] = useState("");

  // Pago
  const [pagado, setPagado] = useState(false);

  // Envío opcional
  const [conEnvio, setConEnvio] = useState(false);
  const [nombreDestinatario, setNombreDestinatario] = useState("");
  const [direccionLinea, setDireccionLinea] = useState("");
  const [telefono, setTelefono] = useState("");
  const [nota, setNota] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cli, prod] = await Promise.all([
          getClientes().catch(() => []),
          getProductos().catch(() => []),
        ]);
        if (!alive) return;
        setClientes(Array.isArray(cli) ? cli : cli?.data || []);
        setProductos(Array.isArray(prod) ? prod : []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Cierra el desplegable de clientes al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!clienteOpen) return undefined;
    const onDown = (e) => {
      if (clienteComboRef.current && !clienteComboRef.current.contains(e.target)) {
        setClienteOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setClienteOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [clienteOpen]);

  useEffect(() => {
    let alive = true;
    obtenerConfiguracionTiendaService().then((res) => {
      if (!alive) return;
      if (res.ok && res.data) setConfiguracionTienda(res.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  const fmt = (v) =>
    formatearPrecio(v, configuracionTienda, { espacioEntreSimbolo: true }) || "—";

  const clientesFiltrados = useMemo(() => {
    const q = clienteSearch.trim().toLowerCase();
    if (!q) return clientes.slice(0, 60);
    return clientes
      .filter((c) => {
        const nom = `${c.nombre || ""} ${c.apellido || ""} ${c.correo || ""} ${c.telefono || ""}`.toLowerCase();
        return nom.includes(q);
      })
      .slice(0, 60);
  }, [clientes, clienteSearch]);

  const clienteSeleccionado = useMemo(
    () => clientes.find((c) => String(c.cliente_id) === String(clienteId)) || null,
    [clientes, clienteId]
  );

  const nombreCliente = (c) =>
    `${c?.nombre || ""} ${c?.apellido || ""}`.trim() || `Cliente #${c?.cliente_id}`;

  const seleccionarCliente = (c) => {
    setClienteId(String(c.cliente_id));
    setClienteSearch("");
    setClienteOpen(false);
  };

  const limpiarCliente = () => {
    setClienteId("");
    setClienteSearch("");
    setClienteOpen(true);
  };

  // Crea un cliente desde el modal reutilizado; recarga la lista y lo autoselecciona.
  const handleCrearCliente = async (form) => {
    const res = await crearCliente(form); // { ok: true, id }
    const nuevoId = res?.id ?? res?.data?.id ?? res?.cliente_id;

    // Cliente recién creado (para asignarlo aunque la lista recargada
    // —ordenada o limitada— aún no lo incluya).
    const nuevoCliente = {
      cliente_id: nuevoId,
      nombre: form?.nombre || "",
      apellido: form?.apellido || "",
      correo: form?.correo || "",
      codigo_telefono: form?.codigo_telefono || "",
      telefono: form?.telefono || "",
    };

    const cli = await getClientes().catch(() => []);
    let list = Array.isArray(cli) ? cli : cli?.data || [];
    if (
      nuevoId != null &&
      !list.some((c) => String(c.cliente_id) === String(nuevoId))
    ) {
      list = [nuevoCliente, ...list];
    }
    setClientes(list);

    if (nuevoId != null) {
      // Asigna automáticamente el cliente creado al pedido.
      setClienteId(String(nuevoId));
      setClienteSearch("");
      setClienteOpen(false);
    }
  };

  // Solo productos activos y con stock disponible
  const productosDisponibles = useMemo(
    () =>
      productos.filter(
        (p) =>
          Number(p.estado) === 1 && Number(p.cantidad_disponible || 0) > 0
      ),
    [productos]
  );

  // Resultados del buscador (filtra por texto; muestra todos los disponibles)
  const productosBuscados = useMemo(() => {
    const q = productoSearch.trim().toLowerCase();
    return productosDisponibles
      .filter((p) => {
        if (!q) return true;
        const txt = `${p.nombre || ""} ${p.no_parte || ""} ${p.sku || ""}`.toLowerCase();
        return txt.includes(q);
      })
      .slice(0, 60);
  }, [productosDisponibles, productoSearch]);

  const itemIds = useMemo(
    () => new Set(items.map((it) => it.producto_id)),
    [items]
  );

  // Clic en una tarjeta: agrega si no está, lo quita si ya estaba.
  const toggleProducto = (p) => {
    const pid = Number(p.producto_id);
    if (itemIds.has(pid)) {
      setItems((prev) => prev.filter((it) => it.producto_id !== pid));
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        producto_id: pid,
        nombre: p.nombre,
        cantidad: 1,
        precio_unitario: precioBaseProducto(p),
        stock: Number(p.cantidad_disponible || 0),
        url_image: p.url_image || null,
      },
    ]);
  };

  const patchItem = (pid, field, value) => {
    setItems((prev) =>
      prev.map((it) => (it.producto_id === pid ? { ...it, [field]: value } : it))
    );
  };

  const quitarItem = (pid) => {
    setItems((prev) => prev.filter((it) => it.producto_id !== pid));
  };

  const total = useMemo(
    () =>
      items.reduce(
        (s, it) => s + Number(it.precio_unitario || 0) * Number(it.cantidad || 0),
        0
      ),
    [items]
  );

  const validar = () => {
    if (!clienteId) return "Seleccioná un cliente.";
    if (!items.length) return "Agregá al menos un producto.";
    for (const it of items) {
      const c = Number(it.cantidad);
      const pr = Number(it.precio_unitario);
      if (!Number.isFinite(c) || c <= 0) return `Cantidad inválida en "${it.nombre}".`;
      if (!Number.isFinite(pr) || pr < 0) return `Precio inválido en "${it.nombre}".`;
    }
    return null;
  };

  const guardar = async () => {
    const msg = validar();
    if (msg) {
      setError(msg);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        cliente_id: Number(clienteId),
        pagado,
        items: items.map((it) => ({
          producto_id: it.producto_id,
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
        })),
        nombre_destinatario: conEnvio ? nombreDestinatario.trim() : null,
        direccion_linea_1: conEnvio ? direccionLinea.trim() : null,
        telefono: conEnvio ? telefono.trim() : null,
        nota: nota.trim() || null,
      };
      const res = await crearPedidoManual(payload);
      if (res?.ok) {
        navigate(ORDERS_PATH, {
          state: {
            createdMsg: res?.message || "Pedido manual creado correctamente.",
          },
        });
      } else {
        setError(res?.message || "No se pudo crear el pedido.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "No se pudo crear el pedido."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="com-page">
      <header className="com-topbar">
          <div className="com-topbar__left">
            <button
              type="button"
              className="com-back"
              onClick={cerrar}
              disabled={saving}
              aria-label="Volver a pedidos"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span>Pedidos</span>
            </button>

            <div className="com-topbar__heading">
              <span className="com-topbar__badge">Nuevo pedido</span>
              <div className="com-topbar__titlerow">
                <h1 className="com-topbar__title">Crear pedido manual</h1>
              </div>
            </div>
          </div>
        </header>

        <div className="com-scroll">
          <div className="com-container">
        {loading ? (
          <div className="com-loading">Cargando datos…</div>
        ) : (
          <>
            {/* Envío opcional + Nota (fila individual a lo ancho, arriba) */}
            <section className="com-card com-envio-row">
              <label className="com-check">
                <input
                  type="checkbox"
                  checked={conEnvio}
                  onChange={(e) => setConEnvio(e.target.checked)}
                />
                <span>Agregar datos de envío (opcional)</span>
              </label>

              <div className="com-envio-fields">
                {conEnvio && (
                  <>
                    <div className="com-field">
                      <label>Destinatario</label>
                      <input
                        className="com-input"
                        value={nombreDestinatario}
                        onChange={(e) => setNombreDestinatario(e.target.value)}
                        placeholder="Nombre de quien recibe"
                      />
                    </div>
                    <div className="com-field">
                      <label>Dirección</label>
                      <input
                        className="com-input"
                        value={direccionLinea}
                        onChange={(e) => setDireccionLinea(e.target.value)}
                        placeholder="Dirección de entrega"
                      />
                    </div>
                    <div className="com-field">
                      <label>Teléfono</label>
                      <input
                        className="com-input"
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                        placeholder="Teléfono de contacto"
                      />
                    </div>
                  </>
                )}

                <div className="com-field">
                  <label>Nota (opcional)</label>
                  <input
                    className="com-input"
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    placeholder="Observación interna del pedido"
                  />
                </div>
              </div>
            </section>

            <div className="com-config">
            {/* Cliente (solo existentes) */}
            <section className="com-card">
              <div className="com-card__title">Cliente</div>

              {clienteSeleccionado ? (
                <div className="com-client-selected">
                  <div className="com-client-selected__info">
                    <span className="com-client-selected__name">
                      {nombreCliente(clienteSeleccionado)}
                    </span>
                    <span className="com-client-selected__meta">
                      {clienteSeleccionado.correo || clienteSeleccionado.telefono || "Sin contacto"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="com-client-selected__change"
                    onClick={limpiarCliente}
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div className="com-combo" ref={clienteComboRef}>
                  <div className="com-combo__row">
                    <input
                      className="com-input"
                      placeholder="Buscar cliente por nombre, correo o teléfono…"
                      value={clienteSearch}
                      onChange={(e) => {
                        setClienteSearch(e.target.value);
                        setClienteOpen(true);
                      }}
                      onMouseDown={() => setClienteOpen((o) => !o)}
                    />
                    {hasPerm("create_customer") && (
                      <button
                        type="button"
                        className="com-combo__add"
                        onClick={() => {
                          setClienteOpen(false);
                          setShowClienteModal(true);
                        }}
                        title="Crear cliente"
                        aria-label="Crear cliente"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {clienteOpen && (
                    <div className="com-combo__menu">
                      {clientesFiltrados.length === 0 ? (
                        <div className="com-combo__empty">
                          {clientes.length === 0
                            ? "No hay clientes registrados."
                            : "Sin coincidencias."}
                        </div>
                      ) : (
                        clientesFiltrados.map((c) => (
                          <button
                            type="button"
                            key={c.cliente_id}
                            className="com-combo__option"
                            onClick={() => seleccionarCliente(c)}
                          >
                            <span className="com-combo__option-text">
                              <span className="com-combo__option-name">
                                {nombreCliente(c)}
                              </span>
                              <span className="com-combo__option-meta">
                                {c.correo || c.telefono || "—"}
                              </span>
                            </span>
                            <span className="com-combo__option-go" aria-hidden="true">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M9 6l6 6-6 6" />
                              </svg>
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Pago */}
            <section className="com-card">
              <div className="com-card__title">Pago</div>
              <div className="com-seg">
                <button
                  type="button"
                  className={`com-seg__btn${!pagado ? " is-active" : ""}`}
                  onClick={() => setPagado(false)}
                >
                  Pendiente
                </button>
                <button
                  type="button"
                  className={`com-seg__btn${pagado ? " is-active" : ""}`}
                  onClick={() => setPagado(true)}
                >
                  Pagado (descuenta stock)
                </button>
              </div>
            </section>
            </div>

            <div className="com-body">
            <div className="com-main">
            {/* Productos */}
            <section className="com-card com-prod-card">
              <div className="com-card__title">
                Productos
                {items.length > 0 && (
                  <span className="com-count">{items.length} en el pedido</span>
                )}
              </div>

              <div className="com-search">
                <input
                  className="com-input com-search__input"
                  placeholder={
                    productosDisponibles.length === 0
                      ? "No hay productos activos con stock"
                      : "Buscar producto por nombre o SKU…"
                  }
                  value={productoSearch}
                  disabled={productosDisponibles.length === 0}
                  onChange={(e) => setProductoSearch(e.target.value)}
                />
                {productoSearch && (
                  <button
                    type="button"
                    className="com-search__clear"
                    onClick={() => setProductoSearch("")}
                    aria-label="Limpiar"
                  >
                    ✕
                  </button>
                )}
              </div>

              {productosDisponibles.length === 0 ? (
                <p className="com-empty">No hay productos activos con stock para vender.</p>
              ) : productosBuscados.length === 0 ? (
                <p className="com-empty">Sin coincidencias para “{productoSearch}”.</p>
              ) : (
                <div className="com-prodgrid">
                  {productosBuscados.map((p) => {
                    const added = itemIds.has(Number(p.producto_id));
                    const img = p.url_image ? fileURL(p.url_image) : null;
                    return (
                      <button
                        type="button"
                        key={p.producto_id}
                        className={`com-prod${added ? " is-added" : ""}`}
                        onClick={() => toggleProducto(p)}
                        title={added ? "Quitar del pedido" : "Agregar al pedido"}
                      >
                        <div className="com-prod__media">
                          {img ? (
                            <img src={img} alt={p.nombre} loading="lazy" />
                          ) : (
                            <span className="com-prod__noimg">Sin imagen</span>
                          )}
                          <span className="com-prod__mark" aria-hidden>
                            {added ? "✓" : "+"}
                          </span>
                        </div>
                        <div className="com-prod__name">{p.nombre}</div>
                        <div className="com-prod__meta">
                          <span className="com-prod__price">{fmt(precioBaseProducto(p))}</span>
                          <span className="com-prod__stock">Stock {Number(p.cantidad_disponible)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

            </section>

            {error && <div className="com-error">{error}</div>}
            </div>

            <aside className="com-side">
            <section className="com-card com-card--cart">
              <div className="com-card__title">
                Resumen del pedido
                {items.length > 0 && <span className="com-count">{items.length}</span>}
              </div>

              {items.length === 0 ? (
                <p className="com-empty">Tocá un producto del catálogo para agregarlo al pedido.</p>
              ) : (
                <div className="com-selected">
                  {items.map((it) => {
                    const thumb = it.url_image ? fileURL(it.url_image) : null;
                    return (
                      <div className="com-item" key={it.producto_id}>
                        <div className="com-item__head">
                          <div className="com-item__thumb">
                            {thumb ? <img src={thumb} alt="" /> : <span>—</span>}
                          </div>
                          <div className="com-item__name">{it.nombre}</div>
                          <button
                            type="button"
                            className="com-item__remove"
                            onClick={() => quitarItem(it.producto_id)}
                            aria-label="Quitar"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="com-item__controls">
                          <label className="com-mini">
                            <span>Cant.{it.stock ? ` (máx ${it.stock})` : ""}</span>
                            <input
                              type="number"
                              min="1"
                              max={it.stock || undefined}
                              className="com-input com-input--num"
                              value={it.cantidad}
                              onChange={(e) => patchItem(it.producto_id, "cantidad", e.target.value)}
                            />
                          </label>
                          <label className="com-mini">
                            <span>Precio</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="com-input com-input--num"
                              value={it.precio_unitario}
                              onChange={(e) =>
                                patchItem(it.producto_id, "precio_unitario", e.target.value)
                              }
                            />
                          </label>
                          <span className="com-item__line">
                            {fmt(Number(it.precio_unitario || 0) * Number(it.cantidad || 0))}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            </aside>
          </div>
          </>
        )}
          </div>
        </div>

        <footer className="com-foot">
          <div className="com-total">
            <span>Total</span>
            <strong>{fmt(total)}</strong>
          </div>
          <div className="com-foot__actions">
            <button type="button" className="com-btn" onClick={cerrar} disabled={saving}>
              Cancelar
            </button>
            <button
              type="button"
              className="com-btn com-btn--primary"
              onClick={guardar}
              disabled={saving || loading}
            >
              {saving ? "Creando…" : "Crear pedido"}
            </button>
          </div>
        </footer>

      <CustomerFormModal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        customerData={null}
        onSubmit={handleCrearCliente}
      />
    </div>
  );
}
